import React from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import MascotImage from "../assets/RRMascot-smaller.png";
import { cn } from '../utils/cn';

const NAV_FLAG_ICON_URL = "https://forzabuilt.com/_vercel/image?url=%2Fimages%2Fmisc%2FFlag%20Icon%20with%20asterisk%201.webp&w=512&q=100";

// The mobile nav also changes color scheme if isInverted is true
const MobileNav = ({ isOpen, onClose, links, currentPage, currentSection }) => {
  const location = useLocation();
  const currentPath = location.pathname;

  // Get the current section type from the URL
  const section = currentSection || currentPath.split('/')[1] || 'household';
  const page = currentPage || currentPath.split('/')[2] || 'home';

  const navItems = [
    { name: 'Home', path: `/${currentSection}` },
    { name: 'Products', path: `/${currentSection}/products` },
    { name: 'News', path: `/${currentSection}/news` },
    { name: 'About', path: `/${currentSection}/about` },
    { name: 'Test', path: '/test' },
  ];

  const navLinks = links || navItems;

  const isActiveLink = (linkPath) => {
    return linkPath === location.pathname || 
           (linkPath === `/${section}` && location.pathname === `/${section}/home`) ||
           (linkPath === `/${section}/products` && page === 'product') ||
           (linkPath === `/${section}/news` && location.pathname.startsWith(`/${section}/news`));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={onClose}
          />
          
          {/* Mobile Navigation Menu */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 h-full w-64 bg-white z-50 shadow-[0_8px_16px_rgba(0,0,0,0.3)]"
          >
            <div className="p-6">
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              >
                <FontAwesomeIcon icon={faTimes} className="w-6 h-6" />
              </button>

              {/* Navigation Links */}
              <nav className="mt-12 flex flex-col items-center">
                <ul className="space-y-8 w-full">
                  {navLinks.map((link, index) => (
                    <motion.li 
                      key={link.name} 
                      className="text-center"
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ 
                        duration: 0.5,
                        delay: index * 0.15,
                        ease: "easeOut"
                      }}
                    >
                      <Link
                        to={link.path}
                        onClick={onClose}
                        className={`text-2xl font-semibold ${
                          isActiveLink(link.path)
                            ? 'text-[#D3242A]'
                            : 'text-gray-800 hover:text-[#D3242A]'
                        } transition-colors`}
                        style={{ fontFamily: "Geogrotesque, sans-serif" }}
                      >
                        {link.name}
                      </Link>
                    </motion.li>
                  ))}
                </ul>
              </nav>

              {/* Flag Image */}
              <motion.div
                className="flex flex-col items-center gap-4 mt-8 pointer-events-auto w-full"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ 
                  duration: 0.5,
                  delay: 0.45,
                  ease: "easeOut"
                }}
              >
                <img
                  src={NAV_FLAG_ICON_URL}
                  alt="Made in America"
                  className="h-14 w-auto object-contain"
                />
              </motion.div>

              {/* Mascot Image - Fixed at bottom */}
              <motion.div
                className="fixed bottom-0 left-0 right-0 flex justify-center pb-8 hidden min-h-[406px]:flex"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ 
                  duration: 0.5,
                  delay: 0.6,
                  ease: "easeOut"
                }}
              >
                <img
                  src={MascotImage}
                  alt="Mr. Rugged Red"
                  className="h-32"
                />
              </motion.div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MobileNav;